import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, StickyNote, Send, Pin, Trash2, Edit2, Check, X, 
  Loader2, Users, Clock, PinOff, Lock, Globe, Search, Filter,
  Plus, MoreVertical, Reply, Heart, Bookmark, Hash, AtSign,
  ChevronDown, Bell, BellOff, Smile, Paperclip
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/api';

const NotesPage = () => {
  const { user, hasPermission } = useAuth();
  const [notes, setNotes] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [newNote, setNewNote] = useState({ content: '', title: '', color: 'yellow', is_private: false });
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [searchQuery, setSearchQuery] = useState('');
  const [noteFilter, setNoteFilter] = useState('all'); // all, pinned, private
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const inputRef = useRef(null);

  // Note colors
  const noteColors = [
    { id: 'yellow', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-500' },
    { id: 'blue', bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
    { id: 'green', bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' },
    { id: 'purple', bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' },
    { id: 'red', bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' },
    { id: 'gold', bg: 'bg-[#D4AF37]/20', border: 'border-[#D4AF37]/30', text: 'text-[#D4AF37]' },
  ];

  const getColorClasses = (colorId) => noteColors.find(c => c.id === colorId) || noteColors[0];

  // Fetch all data
  const fetchData = async () => {
    try {
      const [notesRes, chatRes] = await Promise.all([
        api.get('/notes/?note_type=note'),
        api.get('/notes/?note_type=chat')
      ]);
      setNotes(notesRes.data);
      setChatMessages(chatRes.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Poll for new chat messages
  const pollMessages = async () => {
    if (activeTab !== 'chat') return;
    
    try {
      const lastMessage = chatMessages[0];
      const since = lastMessage?.created_at || '';
      const res = await api.get(`/notes/recent?since=${since}`);
      
      if (res.data.length > 0) {
        setChatMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = res.data.filter(m => !existingIds.has(m.id));
          return [...newMessages.reverse(), ...prev];
        });
      }
    } catch (error) {
      console.error('Polling error:', error);
    }
  };

  useEffect(() => {
    fetchData();
    pollIntervalRef.current = setInterval(pollMessages, 3000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (activeTab === 'chat' && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  // Send chat message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      const payload = {
        content: newMessage.trim(),
        note_type: 'chat'
      };
      
      if (replyingTo) {
        payload.content = `@${replyingTo.author_username} ${payload.content}`;
      }
      
      const res = await api.post('/notes/', payload);
      setChatMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setReplyingTo(null);
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  // Create note
  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) return;
    
    setSending(true);
    try {
      const res = await api.post('/notes/', {
        content: newNote.content.trim(),
        title: newNote.title.trim() || null,
        note_type: 'note',
        is_private: newNote.is_private,
        color: newNote.color
      });
      setNotes(prev => [res.data, ...prev]);
      setNewNote({ content: '', title: '', color: 'yellow', is_private: false });
      setShowNoteForm(false);
      toast.success(newNote.is_private ? 'Nota privada creada' : 'Nota creada');
    } catch (error) {
      toast.error('Error al crear nota');
    } finally {
      setSending(false);
    }
  };

  // Toggle pin
  const handleTogglePin = async (noteId) => {
    try {
      const res = await api.post(`/notes/${noteId}/pin`);
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, is_pinned: res.data.is_pinned } : n
      ));
      toast.success(res.data.is_pinned ? 'Nota fijada' : 'Nota desfijada');
    } catch (error) {
      toast.error('Error al fijar nota');
    }
  };

  // Delete note/message
  const handleDelete = async (id, type) => {
    if (!confirm('¿Eliminar este elemento?')) return;
    
    try {
      await api.delete(`/notes/${id}`);
      if (type === 'chat') {
        setChatMessages(prev => prev.filter(m => m.id !== id));
      } else {
        setNotes(prev => prev.filter(n => n.id !== id));
      }
      toast.success('Eliminado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Update note
  const handleUpdateNote = async (noteId) => {
    if (!editContent.trim()) return;
    
    try {
      await api.put(`/notes/${noteId}`, { content: editContent });
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, content: editContent } : n
      ));
      setEditingNote(null);
      setEditContent('');
      toast.success('Nota actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Filter and sort notes
  const filteredNotes = notes
    .filter(note => {
      if (noteFilter === 'pinned') return note.is_pinned;
      if (noteFilter === 'private') return note.is_private;
      return true;
    })
    .filter(note => 
      searchQuery ? note.content.toLowerCase().includes(searchQuery.toLowerCase()) : true
    )
    .sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  // Stats
  const pinnedCount = notes.filter(n => n.is_pinned).length;
  const privateCount = notes.filter(n => n.is_private).length;

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <MessageSquare className="text-[#D4AF37]" size={28} />
                Notas & Chat
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                Comunicación interna del equipo en tiempo real
              </p>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-3">
              <div className="stat-card px-4 py-2">
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-[#D4AF37]" />
                  <span className="text-lg font-bold text-white">{chatMessages.length}</span>
                  <span className="text-xs text-zinc-500">mensajes</span>
                </div>
              </div>
              <div className="stat-card px-4 py-2">
                <div className="flex items-center gap-2">
                  <StickyNote size={16} className="text-blue-400" />
                  <span className="text-lg font-bold text-white">{notes.length}</span>
                  <span className="text-xs text-zinc-500">notas</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1">
              <TabsTrigger 
                value="chat" 
                className="rounded-md data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black font-bold uppercase text-xs tracking-wider"
                data-testid="tab-chat"
              >
                <MessageSquare size={14} className="mr-2" />
                Chat en Vivo
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-black/20 text-[10px]">
                  {chatMessages.length}
                </span>
              </TabsTrigger>
              <TabsTrigger 
                value="notes"
                className="rounded-md data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black font-bold uppercase text-xs tracking-wider"
                data-testid="tab-notes"
              >
                <StickyNote size={14} className="mr-2" />
                Notas
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-black/20 text-[10px]">
                  {notes.length}
                </span>
              </TabsTrigger>
            </TabsList>

            {/* Chat Tab */}
            <TabsContent value="chat" className="mt-0">
              <div className="admin-card !p-0 flex flex-col h-[600px] overflow-hidden">
                {/* Chat Header */}
                <div className="p-4 border-b border-[#27272A] flex items-center justify-between bg-[#0A0A0A]">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                      <div className="absolute inset-0 w-3 h-3 rounded-full bg-green-500 animate-ping opacity-30" />
                    </div>
                    <div>
                      <span className="font-bold text-white">Chat del Equipo</span>
                      <span className="text-xs text-zinc-500 ml-2">• En línea</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-600 px-2 py-1 rounded bg-zinc-800">
                      Actualización automática cada 3s
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <div 
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-[#0A0A0A] to-[#080808]"
                >
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
                    </div>
                  ) : chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                        <MessageSquare size={32} className="text-[#D4AF37]" />
                      </div>
                      <p className="font-bold text-lg">No hay mensajes aún</p>
                      <p className="text-sm text-zinc-600">¡Sé el primero en escribir!</p>
                    </div>
                  ) : (
                    <>
                      {[...chatMessages].reverse().map((msg, index) => {
                        const isOwn = msg.author_username === user?.username;
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`group flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                            data-testid={`chat-message-${msg.id}`}
                          >
                            {/* Avatar */}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isOwn ? 'bg-[#D4AF37]' : 'bg-zinc-800'
                            }`}>
                              <span className={`font-black text-sm ${isOwn ? 'text-black' : 'text-white'}`}>
                                {msg.author_name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            
                            {/* Message Bubble */}
                            <div className={`flex-1 max-w-[75%] ${isOwn ? 'text-right' : ''}`}>
                              <div className={`inline-block rounded-2xl px-4 py-2.5 ${
                                isOwn 
                                  ? 'bg-[#D4AF37] text-black rounded-tr-sm' 
                                  : 'bg-zinc-800 text-white rounded-tl-sm'
                              }`}>
                                {!isOwn && (
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-sm">{msg.author_name}</span>
                                    <span className="text-xs opacity-60">@{msg.author_username}</span>
                                  </div>
                                )}
                                <p className="break-words whitespace-pre-wrap">{msg.content}</p>
                              </div>
                              <div className={`flex items-center gap-2 mt-1 text-xs text-zinc-600 ${isOwn ? 'justify-end' : ''}`}>
                                <Clock size={10} />
                                {formatDistanceToNow(new Date(msg.created_at), { locale: es, addSuffix: true })}
                                
                                {/* Actions */}
                                <div className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isOwn ? 'order-first' : ''}`}>
                                  <button
                                    onClick={() => {
                                      setReplyingTo(msg);
                                      inputRef.current?.focus();
                                    }}
                                    className="p-1 hover:text-[#D4AF37]"
                                    title="Responder"
                                  >
                                    <Reply size={12} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(msg.id, 'chat')}
                                    className="p-1 hover:text-red-500"
                                    title="Eliminar"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                      <div ref={chatEndRef} />
                    </>
                  )}
                </div>

                {/* Reply indicator */}
                <AnimatePresence>
                  {replyingTo && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-4 py-2 bg-[#D4AF37]/10 border-t border-[#D4AF37]/30 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Reply size={14} className="text-[#D4AF37]" />
                        <span className="text-zinc-400">Respondiendo a</span>
                        <span className="text-[#D4AF37] font-bold">@{replyingTo.author_username}</span>
                      </div>
                      <button onClick={() => setReplyingTo(null)} className="text-zinc-500 hover:text-white">
                        <X size={16} />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-[#27272A] bg-[#0A0A0A]">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <Input
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Escribe un mensaje..."
                        className="bg-zinc-900 border-zinc-800 rounded-full pl-4 pr-12 py-6 focus:border-[#D4AF37]"
                        disabled={sending}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <button type="button" className="p-1.5 text-zinc-500 hover:text-[#D4AF37]">
                          <Smile size={18} />
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      disabled={!newMessage.trim() || sending}
                      className="admin-btn-primary rounded-full h-12 w-12 p-0"
                    >
                      {sending ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="mt-0 space-y-6">
              {/* Notes Header */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar notas..."
                    className="pl-10 bg-[#0A0A0A] border-[#27272A] rounded-md"
                  />
                </div>
                
                {/* Filters */}
                <div className="flex items-center gap-2">
                  <div className="flex bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1">
                    {[
                      { id: 'all', label: 'Todas', count: notes.length },
                      { id: 'pinned', label: 'Fijadas', count: pinnedCount, icon: Pin },
                      { id: 'private', label: 'Privadas', count: privateCount, icon: Lock },
                    ].map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setNoteFilter(f.id)}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md flex items-center gap-1.5 transition-colors ${
                          noteFilter === f.id
                            ? 'bg-[#D4AF37] text-black'
                            : 'text-zinc-500 hover:text-white'
                        }`}
                      >
                        {f.icon && <f.icon size={12} />}
                        {f.label}
                        <span className={`text-[10px] px-1 rounded ${
                          noteFilter === f.id ? 'bg-black/20' : 'bg-zinc-800'
                        }`}>
                          {f.count}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={() => setShowNoteForm(!showNoteForm)}
                    className="admin-btn-primary"
                  >
                    <Plus size={16} className="mr-2" />
                    Nueva Nota
                  </Button>
                </div>
              </div>

              {/* New Note Form */}
              <AnimatePresence>
                {showNoteForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <form onSubmit={handleCreateNote} className="admin-card space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold uppercase text-sm flex items-center gap-2">
                          <StickyNote size={16} className="text-[#D4AF37]" />
                          Nueva Nota
                        </h3>
                        <button 
                          type="button" 
                          onClick={() => setShowNoteForm(false)}
                          className="text-zinc-500 hover:text-white"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      
                      <Input
                        value={newNote.title}
                        onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Título (opcional)"
                        className="bg-[#0A0A0A] border-[#27272A] rounded-md"
                      />
                      
                      <Textarea
                        value={newNote.content}
                        onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Escribe tu nota aquí..."
                        rows={4}
                        className="bg-[#0A0A0A] border-[#27272A] rounded-md resize-none"
                        required
                      />
                      
                      <div className="flex items-center justify-between">
                        {/* Color picker */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500 uppercase tracking-wider">Color:</span>
                          <div className="flex gap-1">
                            {noteColors.map((color) => (
                              <button
                                key={color.id}
                                type="button"
                                onClick={() => setNewNote(prev => ({ ...prev, color: color.id }))}
                                className={`w-6 h-6 rounded-full ${color.bg} ${color.border} border-2 transition-transform ${
                                  newNote.color === color.id ? 'scale-125 ring-2 ring-white/30' : ''
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        
                        {/* Privacy toggle */}
                        <button
                          type="button"
                          onClick={() => setNewNote(prev => ({ ...prev, is_private: !prev.is_private }))}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors ${
                            newNote.is_private
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-zinc-800 text-zinc-400'
                          }`}
                        >
                          {newNote.is_private ? <Lock size={12} /> : <Globe size={12} />}
                          {newNote.is_private ? 'Privada' : 'Pública'}
                        </button>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="ghost" onClick={() => setShowNoteForm(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" disabled={sending || !newNote.content.trim()} className="admin-btn-primary">
                          {sending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                          Crear Nota
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Notes Grid */}
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="admin-card h-48 skeleton" />
                  ))}
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="admin-card text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <StickyNote size={32} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-black uppercase mb-2">Sin notas</h3>
                  <p className="text-zinc-500 mb-6">
                    {searchQuery ? 'No se encontraron notas con ese término' : 'Crea tu primera nota'}
                  </p>
                  {!searchQuery && (
                    <Button onClick={() => setShowNoteForm(true)} className="admin-btn-primary">
                      <Plus size={16} className="mr-2" />
                      Nueva Nota
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredNotes.map((note) => {
                    const colorClasses = getColorClasses(note.color || 'yellow');
                    const isEditing = editingNote === note.id;
                    
                    return (
                      <motion.div
                        key={note.id}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`admin-card group relative overflow-hidden ${colorClasses.border} border-l-4`}
                      >
                        {/* Pin indicator */}
                        {note.is_pinned && (
                          <div className="absolute top-3 right-3">
                            <Pin size={14} className="text-[#D4AF37] fill-[#D4AF37]" />
                          </div>
                        )}
                        
                        {/* Private indicator */}
                        {note.is_private && (
                          <div className="absolute top-3 right-10">
                            <Lock size={14} className="text-purple-400" />
                          </div>
                        )}
                        
                        {/* Content */}
                        <div className="space-y-3">
                          {note.title && (
                            <h4 className={`font-bold ${colorClasses.text}`}>{note.title}</h4>
                          )}
                          
                          {isEditing ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                className="bg-[#0A0A0A] border-[#27272A] rounded-md resize-none text-sm"
                                rows={4}
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  onClick={() => handleUpdateNote(note.id)}
                                  className="admin-btn-primary"
                                >
                                  <Check size={14} className="mr-1" />
                                  Guardar
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingNote(null);
                                    setEditContent('');
                                  }}
                                >
                                  <X size={14} className="mr-1" />
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-zinc-300 text-sm whitespace-pre-wrap line-clamp-6">
                              {note.content}
                            </p>
                          )}
                          
                          {/* Footer */}
                          <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <div className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center">
                                <span className="text-[10px] font-bold">{note.author_name?.charAt(0)}</span>
                              </div>
                              <span>{note.author_name}</span>
                              <span>•</span>
                              <span>{formatDistanceToNow(new Date(note.created_at), { locale: es, addSuffix: true })}</span>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleTogglePin(note.id)}
                                className={`p-1.5 rounded hover:bg-zinc-800 ${
                                  note.is_pinned ? 'text-[#D4AF37]' : 'text-zinc-500 hover:text-[#D4AF37]'
                                }`}
                                title={note.is_pinned ? 'Desfijar' : 'Fijar'}
                              >
                                {note.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingNote(note.id);
                                  setEditContent(note.content);
                                }}
                                className="p-1.5 rounded text-zinc-500 hover:text-blue-400 hover:bg-zinc-800"
                                title="Editar"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(note.id, 'note')}
                                className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                                title="Eliminar"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default NotesPage;
